import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button, Card, Alert } from "react-bootstrap";
import { useParams } from "react-router-dom";
import WebhookList from "./ListWebhooks";
import CreateWebhookModal from "./CreateWebhook";
import { useDispatch } from "react-redux";
import { createAlert } from "../../../redux/reducers/Alerts";
import {
  createWebhook,
  deleteWebhook,
  getWebhooks,
  testWebhook,
  regenerateWebhookSecret,
} from "src/docuclipper/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CopyToClipboard } from "react-copy-to-clipboard";

interface Webhook {
  id: string;
  url: string;
  events: string[];
  createdAt: string;
  contractId: number;
}

const WebhooksPage: React.FC = () => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [regeneratedSecret, setRegeneratedSecret] = useState<{
    webhookId: string;
    secret: string;
  } | null>(null);
  const [newWebhookSecret, setNewWebhookSecret] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const dispatch = useDispatch();

  const fetchWebhooks = async () => {
    try {
      const response = await getWebhooks();
      setWebhooks(response);
    } catch (error) {
      dispatch(
        createAlert({
          message: "Failed to fetch webhooks",
          type: "error",
          timeout: 0,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const handleCreateWebhook = async (values: {
    url: string;
    events: string[];
  }) => {
    try {
      const result = await createWebhook({
        url: values.url,
        events: values.events,
      });
      setNewWebhookSecret(result.secret);
      dispatch(
        createAlert({
          message: "Webhook created successfully",
          type: "success",
          timeout: 5000,
        })
      );
      setShowCreateModal(false);
      fetchWebhooks();
      return result;
    } catch (error) {
      dispatch(
        createAlert({
          message: "Failed to create webhook",
          type: "error",
          timeout: 0,
        })
      );
      throw error;
    }
  };

  const handleDeleteWebhook = async (webhookId: string) => {
    try {
      await deleteWebhook(webhookId);
      dispatch(
        createAlert({
          message: "Webhook deleted successfully",
          type: "success",
          timeout: 5000,
        })
      );
      fetchWebhooks();
    } catch (error) {
      dispatch(
        createAlert({
          message: "Failed to delete webhook",
          type: "error",
          timeout: 0,
        })
      );
    }
  };

  const handleTestWebhook = async (
    webhookId: string,
    eventType: "bank" | "invoice"
  ) => {
    try {
      await testWebhook(webhookId, eventType);
      dispatch(
        createAlert({
          message: "Test webhook sent successfully",
          type: "success",
          timeout: 5000,
        })
      );
    } catch (error) {
      dispatch(
        createAlert({
          message: "Failed to send test webhook",
          type: "error",
          timeout: 0,
        })
      );
    }
  };

  const handleRegenerateSecret = async (webhookId: string) => {
    try {
      const result = await regenerateWebhookSecret(webhookId);
      setRegeneratedSecret({ webhookId, secret: result.secret });
      dispatch(
        createAlert({
          message: "Webhook secret regenerated successfully",
          type: "success",
          timeout: 5000,
        })
      );
      fetchWebhooks();
    } catch (error) {
      dispatch(
        createAlert({
          message: "Failed to regenerate webhook secret",
          type: "error",
          timeout: 0,
        })
      );
    }
  };

  const handleCloseSecretModal = () => {
    setRegeneratedSecret(null);
  };

  const handleCopy = () => {
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <Row>
            <h2>Webhooks</h2>
            <Button variant="link" onClick={() => setShowCreateModal(true)}>
              <FontAwesomeIcon icon="plus" />
              <span className="ml-1">Create Webhook</span>
            </Button>
          </Row>
        </Col>
      </Row>

      {newWebhookSecret && (
        <Alert variant="success" className="mb-4">
          <Alert.Heading>Webhook Created Successfully!</Alert.Heading>
          <p>Your webhook secret is:</p>
          <div className="d-flex align-items-center gap-2 mb-2">
            <code className="flex-grow-1">{newWebhookSecret}</code>
            <CopyToClipboard text={newWebhookSecret} onCopy={handleCopy}>
              <Button variant="outline-primary" size="sm">
                <FontAwesomeIcon icon="copy" className="mr-1" />
                {copySuccess ? "Copied!" : "Copy"}
              </Button>
            </CopyToClipboard>
          </div>
          <p className="mb-0">
            <strong>Important:</strong> Make sure to copy this secret now. You
            won't be able to see it again!
          </p>
          <Button
            variant="link"
            className="p-0 mt-2"
            onClick={() => setNewWebhookSecret(null)}
          >
            Dismiss
          </Button>
        </Alert>
      )}

      <Card>
        <Card.Body>
          <WebhookList
            webhooks={webhooks}
            onDelete={handleDeleteWebhook}
            onTest={handleTestWebhook}
            onRegenerateSecret={handleRegenerateSecret}
            onCloseSecretModal={handleCloseSecretModal}
            regeneratedSecret={regeneratedSecret}
            loading={loading}
          />
        </Card.Body>
      </Card>

      <CreateWebhookModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSubmit={handleCreateWebhook}
      />
    </Container>
  );
};

export default WebhooksPage;
